import { isEmpty } from "lodash";
import { AddressBook } from "./AddressBook";
import { Carrier } from "./Carrier";
import { CarrierConfiguration } from "./CarrierConfiguration";
import { Company } from "./Company";
import { getDictionary } from "../utilities/utilities";
import { Validate } from "./Validate";
import moment from "moment";

const dictionary = getDictionary();

/* ===========================================================================
 * ENTITY DEFINITIONS (INTERFACES)
 * =========================================================================== */

/**
 * @interface Collection
 * Main entity representing a shipment collection request.
 */
export interface Collection {
  id: string;
  creationDate: string;
  creationUser: string;
  lastModifiedDate: string;
  lastModifiedUser: string;
  ownerId: string;
  owner: Company;
  internalReference: string;
  externalReference: string;
  carrierReference: string;
  date: Date;
  morningOpenTime: string;
  morningCloseTime: string;
  afternoonOpenTime: string;
  afternoonCloseTime: string;
  status: string;
  addressBook: AddressBook;
  addressBookId: string;
  carrier: Carrier;
  carrierConfiguration: CarrierConfiguration;
  totalWeight: number;
  numPackages: number;
}

/**
 * @interface CreateCollection
 * Data structure required to create a new Collection.
 */
export interface CreateCollection {
  ownerId: string;
  externalReference?: string;
  date: Date;
  morningOpenTime: string;
  morningCloseTime: string;
  afternoonOpenTime: string;
  afternoonCloseTime: string;
  addressBookId: string;
  carrierCode: string;
  carrierConfigurationId?: string;
  totalWeight: number;
  numPackages: number;
}

/**
 * @interface UpdateCollection
 * Data structure required to update an existing Collection.
 */
export interface UpdateCollection extends CreateCollection {
  id: string;
}

/* ---------------------------------------------------------------------------
 * DATA TRANSFORMATION & CONSTANTS
 * ------------------------------------------------------------------------- */

/**
 * Internal helper to map a full Collection entity to a DTO-ready object.
 * * @param {Collection} collection - The source collection object.
 * @returns {Object} A partial object containing common fields for Create/Update.
 */
const convertedCollection = (collection: Collection) => ({
  ownerId: collection.ownerId,
  externalReference: collection.externalReference,
  date: collection.date,
  morningOpenTime: collection.morningOpenTime,
  morningCloseTime: collection.morningCloseTime,
  afternoonOpenTime: collection.afternoonOpenTime,
  afternoonCloseTime: collection.afternoonCloseTime,
  addressBookId: collection.addressBookId,
  totalWeight: collection.totalWeight,
  numPackages: collection.numPackages,
  carrierCode:
    collection.carrier?.code || collection.carrierConfiguration?.carrierCode,
  carrierConfigurationId: collection.carrierConfiguration
    ? collection.carrierConfiguration.id
    : undefined,
});

/**
 * Prepares a Collection object for a Create API request.
 * * @param {Collection} collection - Full entity.
 * @returns {CreateCollection} The formatted DTO.
 */
export const convertCollectionToCreate = (
  collection: Collection,
): CreateCollection => {
  return {
    ...convertedCollection(collection),
  };
};

/**
 * Prepares a Collection object for an Update API request.
 * * @param {Collection} collection - Full entity.
 * @returns {UpdateCollection} The formatted DTO including ID.
 */
export const convertCollectionToUpdate = (
  collection: Collection,
): UpdateCollection => {
  return {
    id: collection.id,
    ...convertedCollection(collection),
  };
};

/**
 * Default state for a new collection initialization.
 */
export const defaultCollection: Partial<CreateCollection> = {
  date: moment().add(1, "day").startOf("day").toDate(),
  morningOpenTime: moment("09:00", "HH:mm").format("HH:mm"),
  morningCloseTime: moment("13:00", "HH:mm").format("HH:mm"),
  afternoonOpenTime: moment("14:00", "HH:mm").format("HH:mm"),
  afternoonCloseTime: moment("18:00", "HH:mm").format("HH:mm"),
  numPackages: 1,
  totalWeight: 1.0,
};

/* ---------------------------------------------------------------------------
 * VALIDATION LOGIC
 * ------------------------------------------------------------------------- */

/**
 * Performs business logic validation on a Collection entity.
 * * @param {Collection} collection - The entity to validate.
 * @param {boolean} standAlone - Whether the collection is being handled outside a shipment context.
 * @returns {Validate} An object containing the validity status and a map of field errors.
 */
export const validate = (
  collection: Collection,
  standAlone: boolean,
): Validate => {
  const validate: Validate = { isValid: true, errors: {} };

  // 1. Mandatory Field Checks
  if (standAlone && !collection.carrierConfiguration) {
    validate.errors.carrierConfiguration = dictionary.messages.mandatory_field;
  }

  if (!collection.date) {
    validate.errors.date = dictionary.messages.mandatory_field;
  }

  if (!collection.numPackages) {
    validate.errors.numPackages = dictionary.messages.mandatory_field;
  }

  if (!collection.totalWeight) {
    validate.errors.totalWeight = dictionary.messages.mandatory_field;
  }

  if (!collection.morningOpenTime) {
    validate.errors.morningOpenTime = dictionary.messages.mandatory_field;
  }

  if (!collection.morningCloseTime) {
    validate.errors.morningCloseTime = dictionary.messages.mandatory_field;
  }

  // 2. Morning Time Range Validation
  if (
    moment(collection.morningOpenTime).isAfter(
      moment(collection.morningCloseTime),
    )
  ) {
    validate.errors.morningCloseTime =
      dictionary.messages.it_must_be_next_to +
      " " +
      moment(collection.morningOpenTime).format("HH:mm");
  }

  // 3. Afternoon Mandatory Checks
  if (!collection.afternoonOpenTime) {
    validate.errors.afternoonOpenTime = dictionary.messages.mandatory_field;
  }

  if (!collection.afternoonCloseTime) {
    validate.errors.afternoonCloseTime = dictionary.messages.mandatory_field;
  }

  // 4. Afternoon Time Range Validation
  if (
    moment(collection.afternoonOpenTime).isAfter(
      moment(collection.afternoonCloseTime),
    )
  ) {
    validate.errors.afternoonCloseTime =
      dictionary.messages.it_must_be_next_to +
      " " +
      moment(collection.afternoonOpenTime).format("HH:mm");
  }

  // 5. Address Validation
  if (!collection.addressBookId) {
    validate.errors.addressBookId = dictionary.messages.mandatory_field;
  }

  // Final status update
  if (!isEmpty(validate.errors)) {
    validate.isValid = false;
  }

  return validate;
};
